// Thin wrappers around the data sources used by the research UI.
//
// Every request goes through the FastAPI Lambda. The Lambda has the IAM
// permissions to sign OpenSearch calls with SigV4 and to read from the
// archive S3 bucket; the browser never touches either directly.
//
// Local dev: VITE_API_BASE_URL is empty, so requests stay same-origin
// and hit the nginx /api proxy → scraper container's uvicorn.
// Deployed:  VITE_API_BASE_URL points at the Lambda function URL.

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');

const apiPath = (path) => `${API_BASE}${path}`;

const json = (response) => {
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }
  return response.json();
};

const fetchNoStore = (url, options = {}) =>
  fetch(url, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

// ---------------------------------------------------------------------------
// Archive (manifest, progress, analyses, failures)
// ---------------------------------------------------------------------------

const archiveGet = (slug) =>
  fetchNoStore(apiPath(`/api/archive/${slug}?ts=${Date.now()}`))
    .then(json)
    .catch(() => null);

export const loadManifest = () =>
  fetchNoStore(apiPath(`/api/archive/manifest?ts=${Date.now()}`)).then(json);

export const loadProgress = () => archiveGet('progress');
export const loadMissing = () => archiveGet('missing');
export const loadAnalyses = () => archiveGet('analyses');
export const loadAnalyzeProgress = () => archiveGet('analyze-progress');
export const loadFailed = () => archiveGet('failed');

export const startScan = async (payload) => {
  const response = await fetch(apiPath('/api/scans/start'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`scan ${response.status}: ${text.slice(0, 200)}`);
  }
  return response.json();
};

export const loadCoverageForms = () =>
  fetchNoStore(apiPath(`/api/coverage/forms?ts=${Date.now()}`)).then(json);

export const startManualDocumentIngest = async (payload) => {
  const response = await fetch(apiPath('/api/coverage/manual-document'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`manual ingest ${response.status}: ${text.slice(0, 300)}`);
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// Local PDF — served by the Lambda from S3 in deployed envs, from the
// nginx /pdfs alias in docker-compose.
// ---------------------------------------------------------------------------

export const localPdfUrl = (sha256, page) => {
  if (!sha256) return null;
  const base = apiPath(`/api/pdfs/${encodeURIComponent(sha256)}.pdf`);
  return page ? `${base}#page=${encodeURIComponent(page)}` : base;
};

// ---------------------------------------------------------------------------
// Agent / ask
// ---------------------------------------------------------------------------

export const askAgent = async (payload) => {
  const response = await fetch(apiPath('/api/ask'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Agent ${response.status}: ${text.slice(0, 200)}`);
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// Upload extraction — small files post directly to Lambda; large ZIPs/docs
// are uploaded to S3 first, then Lambda pulls and extracts them.
// ---------------------------------------------------------------------------

export const presignExtractionUpload = async (payload) => {
  const response = await fetch(apiPath('/api/extract/presign'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`presign ${response.status}: ${text.slice(0, 200)}`);
  }
  return response.json();
};

export const extractUploadedFile = async (payload) => {
  const response = await fetch(apiPath('/api/extract/s3'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`extract ${response.status}: ${text.slice(0, 300)}`);
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// OpenSearch — proxied through the Lambda, which signs requests with
// SigV4. The first arg of each helper used to be the OpenSearch base URL
// + index; the new signatures keep them so the existing store code
// compiles, but the values are ignored.
// ---------------------------------------------------------------------------

const indexSearch = async (body) => {
  const response = await fetch(apiPath('/api/index/search'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!response.ok) {
    throw new Error(`search ${response.status}`);
  }
  return response.json();
};

export const indexCount = async (_base, _index) => {
  const response = await fetchNoStore(apiPath('/api/index/count'));
  return json(response).then((data) => data.count);
};

export const aggregate = (_base, _index, aggs, filter = null) =>
  indexSearch({
    size: 0,
    query: filter || { match_all: {} },
    aggs,
  });

export const searchChunks = (_base, _index, query, filters = {}, size = 20) => {
  const must = [];
  if (query) {
    must.push({
      multi_match: {
        query,
        fields: ['title^3', 'form_name^2', 'text'],
      },
    });
  } else {
    must.push({ match_all: {} });
  }
  const filterClauses = [];
  if (filters.jurisdiction) {
    filterClauses.push({ term: { jurisdiction_code: filters.jurisdiction } });
  }
  if (filters.docKind) {
    filterClauses.push({ term: { doc_kind: filters.docKind } });
  }
  if (filters.taxYear) {
    filterClauses.push({ term: { tax_year: Number(filters.taxYear) } });
  }
  if (filters.formName) {
    filterClauses.push({ term: { form_name: filters.formName } });
  }
  return indexSearch({
    size,
    query: { bool: { must, filter: filterClauses } },
    highlight: {
      fields: { text: { fragment_size: 220, number_of_fragments: 1 } },
    },
    collapse: { field: 'document_url' },
  });
};

export const fetchDocumentChunks = async (_base, _index, documentUrl) => {
  const data = await indexSearch({
    size: 200,
    query: { term: { document_url: documentUrl } },
    sort: [{ chunk_id: 'asc' }],
  });
  return data.hits?.hits?.map((hit) => hit._source) || [];
};

// ---------------------------------------------------------------------------
// TaxLogic outline generator
// ---------------------------------------------------------------------------

export const loadTaxLogicStates = () =>
  fetchNoStore(apiPath(`/api/taxlogic/states?ts=${Date.now()}`)).then(json);

export const refreshTaxLogicBaselines = () =>
  fetch(apiPath('/api/taxlogic/refresh-baselines'), { method: 'POST' }).then(json);

export const uploadTaxLogicMaster = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(apiPath('/api/taxlogic/upload-master'), {
    method: 'POST',
    body: form,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`upload-master ${response.status}: ${text.slice(0, 200)}`);
  }
  return response.json();
};

export const generateTaxLogic = async (payload) => {
  const response = await fetch(apiPath('/api/taxlogic/generate'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`taxlogic ${response.status}: ${text.slice(0, 400)}`);
  }
  return response.json();
};

export const compareDiff = async (payload) => {
  const response = await fetch(apiPath('/api/compare/diff'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`compare ${response.status}: ${text.slice(0, 400)}`);
  }
  return response.json();
};

export const fetchFormVersions = async (_base, _index, jurisdictionCode, formName) => {
  const data = await indexSearch({
    size: 0,
    query: {
      bool: {
        filter: [
          { term: { jurisdiction_code: jurisdictionCode } },
          { term: { form_name: formName } },
        ],
      },
    },
    aggs: {
      by_year: {
        terms: { field: 'tax_year', size: 10, order: { _key: 'desc' } },
        aggs: {
          by_kind: {
            terms: { field: 'doc_kind', size: 6 },
            aggs: {
              docs: { terms: { field: 'document_url', size: 3 } },
            },
          },
        },
      },
    },
  });
  return data.aggregations?.by_year?.buckets || [];
};
